import {
  useGetSurveyByIdLazyQuery,
  useAddAnswerMutation,
} from "graphql/__generated__/hooks";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Survey } from "types/survey";
import { useNotifications } from "hooks/useNotifications";
import { NotificationSeverity } from "helpers/constants";
import { useAuthUser } from "hooks/useAuthUser";

export type AnswerValue = string | number | number[] | null;

export type SurveyAnswers = Record<string, AnswerValue>;

export const useSurveyAnswer = () => {
  const { surveyId, organizationId } = useParams();
  const navigate = useNavigate();
  const [getSurveyById, { data, loading }] = useGetSurveyByIdLazyQuery();
  const [addAnswer] = useAddAnswerMutation();
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const { notify } = useNotifications();
  const { user } = useAuthUser();

  const survey = useMemo<Survey | null>(() => {
    if (!data?.survey_by_pk) return null;
    return {
      survey_id: data.survey_by_pk.survey_id,
      name: data.survey_by_pk.name || "",
      org_id: data.survey_by_pk.org_id || undefined,
      questions:
        data.survey_by_pk.questions?.map((sq: any) => ({
          survey_question_id: sq.survey_question_id,
          question: {
            question_id: sq.question?.question_id,
            title: sq.question?.title || "",
            type: sq.question?.type || "open_ended",
            body: sq.question?.body || "",
            order: sq.question?.order || 0,
            options:
              sq.question?.options?.map((opt: any) => ({
                option_id: opt.option_id,
                text: opt.text || "",
                order: opt.order || 0,
                question_id: sq.question?.question_id || "",
              })) || [],
          },
        })) || [],
    };
  }, [data]);

  useEffect(() => {
    if (surveyId) {
      getSurveyById({ variables: { surveyId } });
    }
  }, [surveyId, getSurveyById]);

  const updateAnswer = (questionId: string, value: AnswerValue) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!survey) return;

    try {
      // Generate a single timestamp for all answers in this submission
      const submissionTimestamp = new Date().toISOString();

      // Save all answers
      for (const [questionId, answerValue] of Object.entries(answers)) {
        if (answerValue !== null && answerValue !== undefined) {
          const question = survey.questions?.find(
            (sq) => sq.question?.question_id === questionId,
          );
          const questionType = question?.question?.type || "open_ended";

          if (Array.isArray(answerValue)) {
            // Multiple choice - create one answer per selected option
            if (question?.question?.options) {
              const sortedOptions = [...question.question.options].sort(
                (a, b) => (a.order || 0) - (b.order || 0),
              );

              for (const optionIndex of answerValue) {
                const selectedOption = sortedOptions[optionIndex];
                if (selectedOption?.option_id) {
                  await addAnswer({
                    variables: {
                      answer: selectedOption.text,
                      questionId: questionId,
                      optionId: selectedOption.option_id,
                      userId: user?.user_id || null,
                      createdAt: submissionTimestamp,
                    } as any,
                  });
                }
              }
            }
          } else if (typeof answerValue === "number") {
            // Single choice - get option text and option_id by index
            if (question?.question?.options) {
              const sortedOptions = [...question.question.options].sort(
                (a, b) => (a.order || 0) - (b.order || 0),
              );
              const selectedOption = sortedOptions[answerValue];

              if (selectedOption) {
                await addAnswer({
                  variables: {
                    answer: selectedOption.text,
                    questionId: questionId,
                    optionId: selectedOption.option_id,
                    userId: user?.user_id || null,
                    createdAt: submissionTimestamp,
                  } as any,
                });
              }
            }
          } else {
            // Open ended - use string value directly, no option_id
            await addAnswer({
              variables: {
                answer: answerValue,
                questionId: questionId,
                optionId: null,
                userId: user?.user_id || null,
                createdAt: submissionTimestamp,
              } as any,
            });
          }
        }
      }

      notify({
        message: "Survey submitted successfully!",
        severity: NotificationSeverity.success,
      });

      // Navigate back
      navigate(
        organizationId ? `/organization/${organizationId}/surveys` : "/surveys",
      );
    } catch (error: any) {
      notify({
        message: error.message || "Error submitting survey",
        severity: NotificationSeverity.error,
      });
    }
  };

  return {
    survey,
    loading,
    answers,
    updateAnswer,
    handleSubmit,
    organizationId,
  };
};
